// CSR-2063 — where a template's blanks are, and what kind of input each one is.
//
// The editor draws the blank ACORD page onto a canvas and floats a real HTML
// input over every fill-in blank. This module answers "where and what"; the
// component answers "how it looks". Geometry is returned in page units (the
// scale-1 viewport), so zooming multiplies the numbers rather than re-reading the
// PDF.

import { isAcordServerOwnedBlank } from "@shared/acord-forms";

export type AcordFieldKind = "text" | "multiline" | "checkbox" | "radio";

export interface AcordPdfField {
  // Unique per widget. A radio group repeats its field name across options, and
  // a few templates repeat a name across pages, so neither is a usable key.
  key: string;
  // The AcroForm field name — the same string the prefill and the save endpoint
  // use, verified identical between pdf.js and pdf-lib on every template.
  name: string;
  kind: AcordFieldKind;
  // The option this widget selects, for a radio group. Null otherwise.
  option: string | null;
  left: number;
  top: number;
  width: number;
  height: number;
  // A declared /MaxLen. The server silently clips a longer value, so the input
  // has to stop the agent at the same limit rather than let them type into
  // nothing.
  maxLength: number | null;
  readOnly: boolean;
}

// The slice of a pdf.js annotation this module reads. Declared locally because
// pdfjs-dist types the return of `getAnnotations()` as `any[]`.
export interface PdfAnnotationLike {
  subtype?: string;
  fieldType?: string;
  fieldName?: string;
  fieldValue?: unknown;
  rect: number[];
  checkBox?: boolean;
  radioButton?: boolean;
  buttonValue?: string | null;
  multiLine?: boolean;
  maxLen?: number | null;
  readOnly?: boolean;
  hidden?: boolean;
}

interface ViewportLike {
  convertToViewportRectangle(rect: number[]): number[];
}

function fieldKind(annotation: PdfAnnotationLike): AcordFieldKind | null {
  if (annotation.checkBox) return "checkbox";
  if (annotation.radioButton) return "radio";
  // A push button carries no value, so there is nothing for the agent to edit.
  if (annotation.fieldType !== "Tx") return null;
  return annotation.multiLine ? "multiline" : "text";
}

export function collectAcordPdfFields(
  annotations: PdfAnnotationLike[],
  viewport: ViewportLike,
  pageNumber: number,
): AcordPdfField[] {
  const fields: AcordPdfField[] = [];

  annotations.forEach((annotation, index) => {
    if (annotation.subtype !== "Widget") return;
    // A hidden widget has no box on the page — an input floating over whatever is
    // printed there would look like a stray editable box.
    if (annotation.hidden) return;
    const kind = fieldKind(annotation);
    if (kind === null || !annotation.fieldName) return;
    // CSR-2123 — the two blanks naming who signed belong to the server, which
    // draws the signature and prints the name to match it. An input over either
    // would invite an agent to type a name the finished document then contradicts.
    if (isAcordServerOwnedBlank(annotation.fieldName)) return;

    const [x1, y1, x2, y2] = viewport.convertToViewportRectangle(annotation.rect);
    fields.push({
      key: `p${pageNumber}-${index}-${annotation.fieldName}`,
      name: annotation.fieldName,
      kind,
      option: kind === "radio" ? annotation.buttonValue ?? null : null,
      left: Math.min(x1, x2),
      top: Math.min(y1, y2),
      width: Math.abs(x2 - x1),
      height: Math.abs(y2 - y1),
      maxLength: typeof annotation.maxLen === "number" && annotation.maxLen > 0
        ? annotation.maxLen
        : null,
      readOnly: annotation.readOnly === true,
    });
  });

  return fields;
}

/**
 * Text the template itself already carries.
 *
 * The values the editor sends are the whole form, so anything printed in a blank
 * before AMS touches it has to be part of the set or saving would quietly erase
 * it. Across all eight templates that is one blank: the edition marker in the
 * ACORD 125 corner ("ACORD 0125 2016-03r1").
 *
 * Deliberately text only. No template ships a ticked box, and the one radio group
 * (the RPS Cargo App's "Carrier Type") is malformed: the group's value is absent —
 * nothing is selected, which is what both a flattened save and the rendered page
 * show — while its first option's widget carries a stray appearance state saying
 * otherwise. Reading that back would tick an option the finished document does
 * not have.
 */
export function collectAcordTemplateText(
  annotations: PdfAnnotationLike[],
): Record<string, string> {
  const text: Record<string, string> = {};

  for (const annotation of annotations) {
    if (annotation.subtype !== "Widget" || !annotation.fieldName) continue;
    if (annotation.fieldType !== "Tx" || annotation.checkBox || annotation.radioButton) continue;
    if (typeof annotation.fieldValue === "string" && annotation.fieldValue !== "") {
      text[annotation.fieldName] = annotation.fieldValue;
    }
  }

  return text;
}
